import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { format } from "date-fns";
import { CloudDownloadOutlined } from "@mui/icons-material";
import { round } from "lodash";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { CSVLink } from "react-csv";
import {
  SecurityCommission,
  useListAndSearchAllSecurityCommissions,
} from "@rentablo/api/apiv1";
import { toCurrency, toGermanNumberFormat } from "@helpers";
import { Modal } from "@rentablo/components";

export interface BillingDetailsItemProps {
  item: SecurityCommission;
}

const DetailMainBox = styled.div`
  padding: ${({ theme }) => theme.spacing(2, 0)};
  border-bottom: ${({ theme }) =>
    `1px solid ${theme.palette.border.secondary}`};
`;

export const BillingDetailsItem = (props: BillingDetailsItemProps) => {
  const { item } = props;
  const { t } = useTranslation();
  return (
    <DetailMainBox>
      <Typography variant="h6">
        {`${format(new Date(item.billingDate!), "dd.MM.yyyy")}: ${
          item.customerFirstName
        } ${item.customerLastName} (${item.externalCustomerId})`}
      </Typography>
      <Typography variant="body3">
        {`${toCurrency(item.grossCommission)} ${t(
          "domains.cashback.dashboard.details.commission"
        )} ${toGermanNumberFormat(round(item.valuationBase!))} ${t(
          "domains.cashback.dashboard.details.evaluation"
        )}`}
      </Typography>
      <Typography variant="body3">{`Depot: ${item.depotId} (${item.bank?.name})`}</Typography>
      <Typography variant="body3">{`${item.securityName}`}</Typography>
    </DetailMainBox>
  );
};

export interface CashbackDashboardBillingDetailsDialogProps {
  open: boolean;
  selectedValue: number[];
  onClose: () => void;
}

export const CashbackDashboardBillingDetailsDialog = (
  props: CashbackDashboardBillingDetailsDialogProps
) => {
  const { t } = useTranslation();
  const { open, selectedValue, onClose } = props;
  const { data } = useListAndSearchAllSecurityCommissions(
    { id: selectedValue },
    {
      enabled: Boolean(selectedValue.length),
      initialData: { securityCommissions: [] },
    }
  );
  const handleClose = () => {
    onClose();
  };
  const csvData = data!.securityCommissions!.map((i) => ({
    customerFirstName: i.customerFirstName,
    customerLastName: i.customerLastName,
    bank: {
      name: i.bank?.name || "",
    },
    depotId: i.depotId,
    securityCommissions: {
      isin: i.security?.isin || "N/A",
      name: i.security?.name || "N/A",
    },
    date: format(new Date(i.billingDate!), "dd.MM.yyyy"),
    netCommission: i.netCommission,
    valuationBase: i.valuationBase,
    commissionType: i.commissionType,
  }));
  const headers = [
    {
      label: t("domains.cashback.download.customerFirstName"),
      key: "customerFirstName",
    },
    {
      label: t("domains.cashback.download.customerLastName"),
      key: "customerLastName",
    },
    { label: t("domains.cashback.download.bank"), key: "bank.name" },
    { label: t("domains.cashback.download.depotId"), key: "depotId" },
    {
      label: t("domains.cashback.download.securityIsin"),
      key: "securityCommissions.isin",
    },
    {
      label: t("domains.cashback.download.securityName"),
      key: "securityCommissions.name",
    },
    { label: t("domains.cashback.download.date"), key: "date" },
    {
      label: t("domains.cashback.download.netCommission"),
      key: "netCommission",
    },
    {
      label: t("domains.cashback.download.valuationBase"),
      key: "valuationBase",
    },
    {
      label: t("domains.cashback.download.commissionType"),
      key: "commissionType",
    },
  ];
  return (
    <Modal
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      isOpen={open}
      title={t("domains.cashback.dashboard.details.commission")}
      actionButton={
        <CSVLink
          data={csvData}
          headers={headers}
          filename="security-comissions.csv"
        >
          <IconButton color="primary" aria-label="download" size="large">
            <CloudDownloadOutlined />
          </IconButton>
        </CSVLink>
      }
    >
      <Box sx={{ overflowY: "auto" }} height="calc(100% - 68px)">
        {data?.securityCommissions?.map((item) => (
          <BillingDetailsItem item={item} />
        ))}
      </Box>
    </Modal>
  );
};
